import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { RequestForm, RequestFeedback } from 'pages/ServiceRequest/components';
import { Broker } from 'models';
import RoomIcon from '@material-ui/icons/Room';
import CallIcon from '@material-ui/icons/Call';
import EmailIcon from '@material-ui/icons/Email';
import { getColorContrastRatio, getPhoneNumberWithFormatting } from 'utils';
import { images } from 'assets/images';
import { isMobile } from 'utils/screen';
import { COLOR, COLOR_CONTRAST_RATIO } from 'config/constants';
import 'aos/dist/aos.css';

const useStyles = makeStyles({
  content: {
    width: '100%',
    height: '100%',
    margin: '50px auto 50px',
    padding: '50px 0px',
    justifyContent: 'center',
    display: 'flex',
    overflowX: 'hidden',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: '80%',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  brokerImage: {
    width: 350,
    height: 450,
    borderRadius: 20,
    objectFit: 'cover',
    '@media (max-width: 768px)': {
      width: 300,
      height: 400,
    },
  },
  landscape: {
    width: 450,
    height: 350,
    borderRadius: 20,
    objectFit: 'cover',
    '@media (max-width: 768px)': {
      width: 400,
      height: 300,
    },
  },
  formContent: {
    height: '100%',
    padding: '5px 20px',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
  },
  form: {
    justifyContent: 'center',
  },
  formTitle: {
    fontSize: 20,
    margin: '5px auto',
    textTransform: 'uppercase',
  },
  pictureContent: {
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    display: 'flex',
    marginRight: 100,
    '@media (max-width: 768px)': {
      marginRight: 0,
      marginBottom: 20,
    },
  },
  bioContent: {
    marginTop: 10,
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
  },
  addressContent: {
    marginTop: 20,
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
  },
  brokerBio: {
    marginTop: 20,
    alignItems: 'center',
  },
  socialMediaContent: {
    marginTop: 10,
    justifyContent: 'center',
    height: 'auto',
  },
  brokerContactInfos: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 10,
    maxWidth: '40%',
    justifyContent: 'center',
    '@media (max-width: 768px)': {
      maxWidth: '80%',
    },
  },
  brokerContactInfosItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 10,
  },
  subtitleContent: {
    paddingLeft: 20,
  },
  subtitle: {
    fontFamily: 'FordAntenna-Bold',
    fontSize: 24,
    textTransform: 'uppercase',
    marginBottom: 20,
  },
  brokerInfoContent: {
    marginTop: 20,
    maxWidth: 500,
    '@media (max-width: 768px)': {
      maxWidth: 300,
    },
  },
  name: {
    fontSize: 20,
    fontFamily: 'FordAntenna-SemiBold',
  },
  iconRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 5,
    marginTop: 5,
    flexDirection: 'row',
  },
  icon: {
    marginRight: 10,
  },
  infoText: {
    wordBreak: 'break-word',
    fontSize: 14,
    '@media (max-width: 768px)': {
      fontSize: 13,
    },
  },
  addressRow: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
  },
});

interface Props {
  className?: string;
  broker: Broker;
  brokerAddress?: string;
  displayBio?: boolean;
  bio?: string;
  portal_infos?: any;
  showServiceOptions?: boolean;
}

function BrokerForm({
  className,
  broker,
  brokerAddress,
  displayBio,
  bio,
  portal_infos,
  showServiceOptions,
}: Props) {
  const styles = useStyles();
  const location = useLocation();
  const intl = useIntl();
  const { pathname } = location;
  const [isSubmited, setIsSubmited] = useState(false);
  const [imageRatio, setImageRatio] = useState(1);

  const secondary_color = portal_infos?.template_style?.secondary_color || COLOR.SECONDARY_BLUE;
  const primary_color_contrast_ratio = getColorContrastRatio(
    portal_infos?.template_style?.primary_color,
    COLOR.WHITE,
  );
  const secondary_color_contrast_ratio = getColorContrastRatio(secondary_color, COLOR.WHITE);
  const primary_bg_text_color =
    primary_color_contrast_ratio > COLOR_CONTRAST_RATIO ? COLOR.WHITE : COLOR.BLACK;
  const secondary_bg_text_color =
    secondary_color_contrast_ratio > COLOR_CONTRAST_RATIO ? COLOR.WHITE : COLOR.BLACK;
  const brokerAdress = portal_infos?.address || broker?.centrisOfficeAddress;

  const fmtContactMe = intl.formatMessage({ id: 'portal.contactMe' });
  const fmtGetInTouch = intl.formatMessage({ id: 'portal.getInTouch' });

  useEffect(() => {
    const image = new Image();
    image.src = portal_infos?.broker?.logo_url || images.broker;
    image.onload = () => {
      setImageRatio(image.width / image.height);
    };
  }, [portal_infos]);
  return (
    <div
      className={classNames('section contactform', styles.content)}
      style={{ color: primary_bg_text_color }}
      data-section='contactform'
    >
      <div className={!isMobile() ? styles.row : styles.column}>
        {portal_infos.broker ? (
          <div className={styles.pictureContent}>
            {portal_infos.broker?.logo_url ? (
              <img
                src={portal_infos.broker?.logo_url || images.broker}
                alt='broker'
                className={imageRatio > 1 ? styles.landscape : styles.brokerImage}
              />
            ) : null}
            <div className={styles.brokerInfoContent}>
              <Typography className={styles.name}>
                {`${broker.firstname} ${broker.lastname}`}
              </Typography>
              {((portal_infos && portal_infos.phone_number) || broker.centrisPhoneNumber) && (
                <div className={styles.iconRow}>
                  <CallIcon className={styles.icon} />
                  <Typography className={styles.infoText}>
                    {getPhoneNumberWithFormatting(
                      portal_infos.phone_number || broker.centrisPhoneNumber,
                    )}
                  </Typography>
                </div>
              )}
              {portal_infos && portal_infos.email && (
                <div className={styles.iconRow}>
                  <EmailIcon className={styles.icon} />
                  <Typography
                    onClick={() => {
                      window.location.href = `mailto:${portal_infos.email}`;
                    }}
                    className={styles.infoText}
                    style={{
                      cursor: 'pointer',
                      fontSize: 16,
                      fontWeight: 700,
                    }}
                  >
                    {portal_infos.email}
                  </Typography>
                </div>
              )}
              {brokerAdress && (
                <div className={styles.addressRow}>
                  <RoomIcon className={styles.icon} />
                  <Typography className={styles.infoText}>{brokerAdress}</Typography>
                </div>
              )}
            </div>
          </div>
        ) : null}
        <div className={styles.formContent}>
          {isSubmited ? (
            <RequestFeedback />
          ) : (
            <div>
              <div>
                <Typography className={styles.formTitle}>{fmtContactMe}</Typography>
                <div
                  className={styles.subtitleContent}
                  style={{ borderLeft: `4px solid ${secondary_color}` }}
                >
                  <Typography className={styles.subtitle}>{fmtGetInTouch}</Typography>
                </div>
              </div>
              <RequestForm
                className={styles.form}
                isBroker={!!pathname.includes('broker')}
                setIsSubmited={setIsSubmited}
                showHeader={false}
                broker={broker || null}
                portal_infos={portal_infos}
                primary_bg_text_color={primary_bg_text_color}
                secondary_bg_text_color={secondary_bg_text_color}
                showServiceOptions={showServiceOptions}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BrokerForm;
